import { useState, useCallback, useEffect } from "react";
import { Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { NewsDashboard } from "./components/NewsDashboard";
import { RssFeedView } from "./components/RssFeedView";
import { QuarantineView } from "./components/QuarantineView";
import { SettingsView, loadFilterConfig } from "./components/SettingsView";
import type { FilterConfig } from "./components/SettingsView";
import { SystemStatus } from "./components/SystemStatus";
import { WebScraperView } from "./components/WebScraperView";
import { RssFeedReaderPage } from "./components/RssFeedReaderPage";
import { useStats } from "./hooks/useStats";
import { apiFetch } from "./api/client";
import "./styles/infohub.css";

const TAGS_KEY = "infohub_custom_tags";
const DEFAULT_TAGS = ["NIS2", "DORA", "DSGVO", "CRA", "AI-Act", "eIDAS", "MiCA", "BSI", "ENISA", "EBA", "EZB", "Warnung"];

function getConfiguredTags(): string[] {
  try {
    const saved = localStorage.getItem(TAGS_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_TAGS;
  } catch {
    return DEFAULT_TAGS;
  }
}

// Header-Banner (wie CRM/Outlook)
function InfoHubHeader({ title, subtitle, newsletterEmail }: { title: string; subtitle?: string; newsletterEmail?: string }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      padding: '1.25rem 1.75rem',
      borderRadius: 'var(--radius-md)',
      marginBottom: '1.25rem',
      display: 'flex', alignItems: 'center',
    }}>
      <div style={{ flex: '0 0 auto' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', margin: '0.25rem 0 0' }}>{subtitle}</p>}
      </div>
      {newsletterEmail && (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 1rem', borderRadius: '999px',
          background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
          fontSize: '0.8125rem',
        }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="14" height="10" rx="1" />
            <path d="M1 4l7 5 7-5" />
          </svg>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>Newsletter:</span>
          <a href={`mailto:${newsletterEmail}`} style={{
            fontWeight: 600, color: 'white', textDecoration: 'none',
            fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: '0.8125rem',
          }}>
            {newsletterEmail}
          </a>
        </div>
        </div>
      )}
    </div>
  );
}

// Register-Tabs fuer Einstellungen
const SETTINGS_TABS = [
  { to: '/infohub/admin/quellen', label: 'Quellen' },
  { to: '/infohub/admin/quarantaene', label: 'Quarantäne' },
  { to: '/infohub/admin/einstellungen', label: 'Einstellungen' },
  { to: '/infohub/admin/system', label: 'Statistik' },
];

// Unter-Tabs fuer Quellen
const SOURCE_TABS = [
  { key: 'email' as const, label: 'E-Mail' },
  { key: 'rss' as const, label: 'RSS Feeds' },
  { key: 'scraper' as const, label: 'Web-Scraper' },
];

function QuellenPage() {
  const [activeSource, setActiveSource] = useState<'email' | 'rss' | 'scraper'>('email');
  return (
    <div>
      <div style={{
        display: 'flex', gap: '0.25rem', marginBottom: '1rem',
        background: 'var(--bg-surface, #f1f5f9)', borderRadius: 'var(--radius, 8px)', padding: '3px',
        width: 'fit-content',
      }}>
        {SOURCE_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSource(tab.key)}
            style={{
              padding: '0.5rem 1rem', fontSize: '0.8125rem', fontWeight: activeSource === tab.key ? 600 : 400,
              background: activeSource === tab.key ? 'var(--bg-card, white)' : 'transparent',
              color: activeSource === tab.key ? 'var(--text, #0f172a)' : 'var(--text-muted, #64748b)',
              border: 'none', borderRadius: 'var(--radius, 6px)', cursor: 'pointer',
              boxShadow: activeSource === tab.key ? 'var(--shadow, 0 1px 3px rgba(0,0,0,0.1))' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeSource === 'email' && <RssFeedView />}
      {activeSource === 'rss' && <RssFeedView rssOnly />}
      {activeSource === 'scraper' && <WebScraperView />}
    </div>
  );
}

function SettingsTabBar() {
  const loc = useLocation();
  return (
    <div style={{
      display: 'flex', gap: '0', borderBottom: '1px solid var(--color-border)',
      marginBottom: '1.25rem', overflow: 'auto',
    }}>
      {SETTINGS_TABS.map((tab) => {
        const isActive = loc.pathname.startsWith(tab.to);
        return (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={() => ''}
          style={() => ({
            padding: '0.75rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            borderBottom: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s',
          })}
        >
          {tab.label}
        </NavLink>
        );
      })}
    </div>
  );
}

export function InfoHubPage() {
  const [activeTag, setActiveTag] = useState<string | undefined>(undefined);
  const [, setTags] = useState<string[]>(getConfiguredTags);
  const [filterConfig, setFilterConfig] = useState<FilterConfig>(loadFilterConfig);
  const [newsletterEmail, setNewsletterEmail] = useState<string>("");

  useEffect(() => {
    apiFetch<{ email: string }>("/settings/newsletter-email")
      .then((d) => setNewsletterEmail(d.email || ""))
      .catch(() => {});
  }, []);

  const { stats } = useStats();

  const handleTagsChange = useCallback((newTags: string[]) => {
    setTags(newTags);
    localStorage.setItem(TAGS_KEY, JSON.stringify(newTags));
  }, []);

  return (
    <div className="infohub-module">
      <Routes>
        {/* News Feed — Hauptseite (volles Layout wie Original) */}
        <Route index element={
          <>
            <InfoHubHeader title="Info Hub" subtitle={`${stats?.total_items ?? 0} Artikel`} newsletterEmail={newsletterEmail || undefined} />
            <div className="feed-column">
              <NewsDashboard
                activeTag={activeTag}
                onTagChange={setActiveTag}
                filterConfig={filterConfig}
              />
            </div>
          </>
        } />

        {/* RSS Feed Reader — Timeline mit dynamischen Tabs */}
        <Route path="feeds" element={
          <>
            <InfoHubHeader title="RSS Feeds" subtitle="Timeline aller konfigurierten RSS-Quellen" />
            <RssFeedReaderPage />
          </>
        } />

        {/* Einstellungen — mit Register-Tabs */}
        <Route path="admin/*" element={
          <>
            <InfoHubHeader title="Info Hub Einstellungen" subtitle="Quellen, Scraper, Filter und Systemstatus verwalten" />
            <SettingsTabBar />
            <Routes>
              <Route index element={<Navigate to="/infohub/admin/quellen" replace />} />
              <Route path="feeds" element={<Navigate to="/infohub/admin/quellen" replace />} />
              <Route path="quellen" element={<QuellenPage />} />
              <Route path="quarantaene" element={<QuarantineView />} />
              <Route path="einstellungen" element={<SettingsView onTagsChange={handleTagsChange} onEmailChange={() => {}} onFilterConfigChange={setFilterConfig} />} />
              <Route path="system" element={<SystemStatus />} />
            </Routes>
          </>
        } />
      </Routes>
    </div>
  );
}
